/**
 * @file        src/lib/useWalletBalance.js
 * @description The Cash Balance rule, in one place: a tenant_admin sees their
 *              live supplier (TripJack) balances; a super_admin sees "NA" and
 *              NO request is made.
 *
 *              Exposes the supplier's FOUR figures, the way TripJack's own
 *              dashboard states them — `deposit` (prepaid money parked with
 *              them, which is what "Cash Balance" has always meant here),
 *              `credit` (credit still available to draw), `dues` (credit drawn
 *              and owed) and `total`. Each is `{ value, display }`, and a
 *              figure the supplier did not report has value null and displays
 *              "Unavailable" — never "₹0.00", which is a real and very
 *              different answer.
 *
 *              Both the dashboard KPI and the Topbar pill call this. They
 *              share one query key, so React Query dedupes them into a single
 *              network call per load — mounting the hook twice does not fetch
 *              twice.
 *
 *              `enabled` is what actually prevents the super_admin request:
 *              React Query never runs the queryFn while it is false. The
 *              server enforces the same rule independently — /dashboard/* is
 *              authorize('tenant_admin'), so a super_admin gets a 403 there
 *              regardless of what the client does.
 * @author      Kinetika Labs
 * @created     2026-07-30
 * @changelog
 *   - 2026-09-24 Claude — Pass through `creditDerived` / `noCreditLine` so the
 *                          Supplier Account panel can print ₹0.00 credit and
 *                          dues for an account with no credit facility (and say
 *                          so) instead of "Unavailable".
 *   - 2026-07-30 Claude — Initial (live TripJack cash balance).
 *   - 2026-09-21 Claude — Expose deposit / credit / dues / total, not just the
 *                          one number. `display` and `balance` are unchanged
 *                          (both still the deposit figure), so the Topbar pill,
 *                          the dashboard KPI and the Supplier List column keep
 *                          reading exactly what they read before.
 *   - 2026-08-04 Claude — staff also fetches (dashboard.routes.js allows them
 *                          through with no module gate — Cash Balance is part
 *                          of the general dashboard, not an assignable
 *                          module). Without this a staff account showed an
 *                          eternal loading skeleton: shouldFetch was false
 *                          forever for any role that wasn't tenant_admin, not
 *                          just during the brief null-role bootstrap window.
 */
import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client';
import { qk } from '../api/queryKeys';
import { inrExact } from './format';
import { useAuth } from './auth.store';

export const NA = 'NA';
export const UNAVAILABLE = 'Unavailable';

/**
 * One figure the supplier may or may not have stated.
 *
 * The null check is explicit and comes FIRST because `Number(null)` is 0 — a
 * truthiness or Number.isFinite test alone would turn "TripJack didn't tell us"
 * into a confident "₹0.00".
 *
 * @param   {number|null|undefined} raw
 * @param   {string} fallback  what to show when there is no number ("NA"/"Unavailable")
 * @returns {{value: number|null, display: string}}
 */
function figure(raw, fallback) {
  if (raw === null || raw === undefined || raw === '') return { value: null, display: fallback };
  const n = Number(raw);
  return Number.isFinite(n) ? { value: n, display: inrExact(n) } : { value: null, display: fallback };
}

const noFigures = (fallback) => ({
  deposit: { value: null, display: fallback },
  credit: { value: null, display: fallback },
  dues: { value: null, display: fallback },
  total: { value: null, display: fallback },
});

/**
 * @returns {{display: string|null, isLoading: boolean, isSuperAdmin: boolean,
 *            balance: number|null, deposit: object, credit: object,
 *            dues: object, total: object, creditDerived: boolean,
 *            duesDerived: boolean, noCreditLine: boolean}}
 *          `display` is null only while loading, so callers can show their own
 *          skeleton; otherwise it is the formatted amount, "NA" or "Unavailable".
 *          `display`/`balance` are the DEPOSIT figure — the same number this
 *          hook has always returned.
 */
export function useWalletBalance() {
  const role = useAuth((s) => s.user?.role);
  const isSuperAdmin = role === 'super_admin';
  // Wait for the role before deciding — firing on a null role during bootstrap
  // would send exactly the request a super_admin must never make.
  const shouldFetch = role === 'tenant_admin' || role === 'staff';

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.dashboard.walletBalance,
    queryFn: () => get('/dashboard/wallet-balance').then((r) => r.data),
    enabled: shouldFetch,
    // Once per dashboard load. The supplier balance is not worth re-requesting
    // on every window focus, and a failed call must not be retried into a
    // storm — the card is designed to degrade instead.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (isSuperAdmin) {
    return { display: NA, isLoading: false, isSuperAdmin: true, balance: null, duesDerived: false, creditDerived: false, noCreditLine: false, ...noFigures(NA) };
  }
  if (!shouldFetch || isLoading) {
    return { display: null, isLoading: true, isSuperAdmin: false, balance: null, duesDerived: false, creditDerived: false, noCreditLine: false, ...noFigures(UNAVAILABLE) };
  }
  if (isError) {
    return { display: UNAVAILABLE, isLoading: false, isSuperAdmin: false, balance: null, duesDerived: false, creditDerived: false, noCreditLine: false, ...noFigures(UNAVAILABLE) };
  }

  // `balance` is the server's long-standing name for the deposit figure; the
  // breakdown repeats it as `deposit` so a caller reading the four figures
  // together doesn't have to know that.
  const balance = Number(data?.balance);
  // A real zero balance must render "₹0.00", never "Unavailable" — so this
  // checks for a finite number, not for truthiness.
  if (!Number.isFinite(balance)) {
    return { display: UNAVAILABLE, isLoading: false, isSuperAdmin: false, balance: null, duesDerived: false, creditDerived: false, noCreditLine: false, ...noFigures(UNAVAILABLE) };
  }
  return {
    display: inrExact(balance),
    isLoading: false,
    isSuperAdmin: false,
    balance,
    deposit: figure(data?.deposit ?? balance, UNAVAILABLE),
    credit: figure(data?.credit, UNAVAILABLE),
    dues: figure(data?.dues, UNAVAILABLE),
    total: figure(data?.total, UNAVAILABLE),
    // True when a figure was derived rather than stated: credit from
    // total − wallet, dues from limit − available.
    creditDerived: Boolean(data?.creditDerived),
    duesDerived: Boolean(data?.duesDerived),
    // True when the supplier states no credit facility at all — credit and
    // dues are then a real ₹0.00 each, and the UI says why.
    noCreditLine: Boolean(data?.noCreditLine),
  };
}
